import { useEffect, useRef, useState } from "react";
import { useConfiguracao } from "../lib/consultas";
import { pareceLeitor } from "../lib/leitor";
import { sinalErro } from "../lib/sinais";

/**
 * Faixa laranja de leitura (cor da marca Oficial). O leitor (coletora ou pistola USB/Bluetooth) trabalha
 * em modo teclado: digita o código e manda Enter. O campo fica sempre focado e não
 * abre o teclado virtual. Código digitado à mão ou colado é recusado, a não ser
 * que o admin ligue "Permitir digitar o código na faixa de leitura" (teste ou
 * emergência).
 */
export function LeituraCodigo({
  rotulo, onLeitura, desabilitado = false, lampejo = 0, erro = 0, manterFoco = true, aceitarTab = true,
}: {
  rotulo: string;
  onLeitura: (codigo: string) => void;
  desabilitado?: boolean;
  /** muda de valor a cada leitura confirmada: dispara o lampejo verde */
  lampejo?: number;
  /** muda de valor a cada erro: faz a faixa tremer */
  erro?: number;
  manterFoco?: boolean;
  aceitarTab?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [valor, setValor] = useState("");
  const [recusa, setRecusa] = useState<string | null>(null);
  const faixa = useRef<HTMLDivElement>(null);
  const instantes = useRef<number[]>([]);
  const digitacao = useConfiguracao<boolean>("leitura_permitir_digitacao", false);
  const permitirDigitar = digitacao.data === true;

  useEffect(() => {
    if (!desabilitado) ref.current?.focus();
  }, [desabilitado, rotulo]);

  useEffect(() => {
    if (!lampejo || !faixa.current) return;
    faixa.current.classList.remove("lampejo-ok");
    void faixa.current.offsetWidth;
    faixa.current.classList.add("lampejo-ok");
  }, [lampejo]);

  useEffect(() => {
    if (!erro || !faixa.current) return;
    tremer();
  }, [erro]);

  useEffect(() => {
    if (!recusa) return;
    const t = setTimeout(() => setRecusa(null), 4000);
    return () => clearTimeout(t);
  }, [recusa]);

  function tremer() {
    if (!faixa.current) return;
    faixa.current.classList.remove("tremor");
    void faixa.current.offsetWidth;
    faixa.current.classList.add("tremor");
  }

  function recusar(mensagem: string) {
    setValor("");
    instantes.current = [];
    sinalErro();
    tremer();
    setRecusa(mensagem);
  }

  function enviar() {
    const codigo = valor.trim();
    const tempos = instantes.current;
    setValor("");
    instantes.current = [];
    if (!codigo || desabilitado) return;
    if (!permitirDigitar && !pareceLeitor(tempos)) {
      recusar("Código digitado à mão não é aceito. Use o leitor.");
      return;
    }
    setRecusa(null);
    onLeitura(codigo);
  }

  return (
    <div ref={faixa} className="rounded-lg bg-faixa p-3 shadow-[0_-2px_0_0_var(--color-tinta)_inset]">
      <label className="flex flex-col gap-2">
        <span className="font-sinal text-2xl font-bold leading-none">{rotulo}</span>
        <input
          ref={ref}
          value={valor}
          disabled={desabilitado}
          onChange={(e) => {
            if (!e.target.value) instantes.current = [];
            setValor(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || (aceitarTab && e.key === "Tab" && valor.trim())) {
              e.preventDefault();
              enviar();
              return;
            }
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) instantes.current.push(e.timeStamp);
          }}
          onPaste={(e) => {
            if (permitirDigitar) return;
            e.preventDefault();
            recusar("Colar código não é aceito. Use o leitor.");
          }}
          onDrop={(e) => { if (!permitirDigitar) e.preventDefault(); }}
          onBlur={() => {
            if (!manterFoco || desabilitado) return;
            setTimeout(() => {
              const ativo = document.activeElement;
              const outroCampo = ativo && ativo !== ref.current &&
                ["INPUT", "TEXTAREA", "SELECT"].includes(ativo.tagName);
              const dialogo = document.querySelector("[data-dialogo-aberto]");
              if (!outroCampo && !dialogo) ref.current?.focus();
            }, 200);
          }}
          inputMode={permitirDigitar ? "text" : "none"}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-label={rotulo}
          className="min-h-14 w-full rounded-md border-2 border-tinta bg-chapa px-3 font-sinal text-2xl tracking-wide focus:outline-none disabled:opacity-60"
        />
      </label>
      {recusa && <p className="mt-2 rounded-md bg-erro px-3 py-2 font-semibold text-white" role="alert">{recusa}</p>}
      {permitirDigitar && <p className="mt-1 text-sm font-semibold">Digitação liberada pelo admin (só para teste).</p>}
    </div>
  );
}
